import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Code2, Smartphone, Bot,
  Megaphone, BadgeDollarSign, CreditCard
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const groups = [
  {
    n:'01', title:'BUILD', icon: Code2,
    lead:'Construimos productos digitales que operan, venden y escalan.',
    items:[
      'Desarrollo web y sitios corporativos','Landing pages y e-commerce','Aplicaciones web',
      'Aplicaciones móviles iOS / Android','Software a medida','Plataformas digitales',
      'Marketplaces','CRM y sistemas administrativos','Paneles de administración',
      'Sistemas de reservaciones y cotización'
    ]
  },
  {
    n:'02', title:'AUTOMATE', icon: Bot,
    lead:'Conectamos procesos, equipos y datos para operar con mayor eficiencia.',
    items:[
      'Inteligencia artificial','Asistentes conversacionales','Automatización de procesos',
      'Automatización comercial','Integraciones con APIs','Workflows inteligentes',
      'Conexión entre sistemas','Analítica y datos'
    ]
  },
  {
    n:'03', title:'GROW', icon: Megaphone,
    lead:'Convertimos tecnología, marca y contenido en adquisición y crecimiento.',
    items:[
      'Estrategia digital','Creación e impulso de marca','Content Strategy','Social Media',
      'Community Management','Reels y video','Campañas y pauta','Captación de leads',
      'Funnels / embudos de venta','CRM comercial','Automatización de seguimiento','Posicionamiento'
    ]
  },
  {
    n:'04', title:'MONETIZE', icon: CreditCard,
    lead:'Diseñamos la infraestructura para convertir atención en ingresos.',
    items:[
      'E-commerce','Marketplaces','Membresías','Suscripciones','Reservaciones',
      'Cotizaciones','Stripe y pasarelas de pago','Links de pago','Anticipos',
      'Pagos parciales','Conversión de leads','Sistemas digitales de venta'
    ]
  }
];

const process = [
  ['01','ESTRATEGIA','Definimos objetivos, alcance, usuarios y oportunidad de negocio.'],
  ['02','UX/UI','Diseñamos la experiencia y la interfaz con foco en conversión y operación.'],
  ['03','DESARROLLO','Construimos el producto, integraciones, automatizaciones y lógica.'],
  ['04','LANZAMIENTO','Publicamos, validamos flujos críticos y preparamos la operación real.'],
  ['05','SOPORTE','Mantenemos, medimos y evolucionamos la plataforma conforme crece.']
];

const flow = ['IDEA','MARCA','PLATAFORMA','CONTENIDO','AUDIENCIA','LEADS','CONVERSIÓN','PAGO','DATOS','ESCALAMIENTO'];

export default function TechnologyPage(){
  return (
    <Layout>
      <section className="gs-page-hero min-h-[82vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/brand-assets/digital-ecosystem.webp" alt="" className="w-full h-full object-cover opacity-25 gs-photo-monochrome"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-[#030303]/90 to-[#030303]/45"/>
        </div>
        <div className="gs-grid-overlay"/>
        <div className="container-premium relative z-10 pt-32 pb-20">
          <div className="max-w-5xl">
            <p className="section-kicker mb-5">SMART TECHNOLOGY · DIVISIÓN PRINCIPAL</p>
            <h1 className="gs-display text-5xl md:text-7xl xl:text-8xl leading-[.95] mb-8">
              CREAMOS LA INFRAESTRUCTURA DIGITAL<br/>
              <span className="metal-text">QUE CONVIERTE IDEAS EN NEGOCIOS.</span>
            </h1>
            <p className="text-white/62 text-lg md:text-xl max-w-3xl leading-relaxed mb-10">
              Diseñamos y desarrollamos soluciones digitales a medida para vender, automatizar y operar con mayor eficiencia.
            </p>
            <Link to="/contact" className="btn-metal inline-flex items-center gap-2">
              Solicitar diagnóstico <ArrowRight size={17}/>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="grid gap-6">
            {groups.map(({n,title,icon:Icon,lead,items})=>(
              <motion.article
                key={title}
                initial={{opacity:0,y:28}}
                whileInView={{opacity:1,y:0}}
                viewport={{once:true,margin:'-80px'}}
                transition={{duration:.65}}
                className="editorial-frame gs-tech-row p-8 md:p-12"
              >
                <div className="grid lg:grid-cols-[.3fr_.7fr_1.3fr] gap-10">
                  <div className="flex lg:block items-center gap-4">
                    <span className="text-white/28 tracking-[.3em] text-xs">{n}</span>
                    <Icon className="text-[#d7dbe0] mt-0 lg:mt-8" size={26}/>
                  </div>
                  <div>
                    <h2 className="font-accent text-2xl md:text-3xl tracking-[.16em] text-white mb-5">{title}</h2>
                    <p className="text-white/50 leading-relaxed">{lead}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {items.map(item=><div key={item} className="service-line">{item}</div>)}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/7">
        <div className="container-premium">
          <div className="editorial-frame overflow-hidden">
            <img src="/brand-assets/tech-web-apps-software.jpg" alt="Desarrollo web, apps y software" className="w-full h-auto block"/>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="editorial-frame overflow-hidden">
            <img src="/brand-assets/tech-advanced-stack.jpg" alt="Advanced Technology Stack" className="w-full h-auto block"/>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#090909] border-y border-white/7">
        <div className="container-premium">
          <div className="grid lg:grid-cols-[.7fr_1.3fr] gap-14 items-start">
            <div>
              <p className="section-kicker mb-4">DE ESTRATEGIA A OPERACIÓN</p>
              <h2 className="gs-display text-4xl md:text-6xl">NUESTRO PROCESO <span className="metal-text">DIGITAL</span></h2>
            </div>
            <div className="border-t border-white/15">
              {process.map(([n,title,text])=>(
                <div key={n} className="grid grid-cols-[52px_1fr] md:grid-cols-[70px_.65fr_1.35fr] gap-5 py-7 border-b border-white/10 items-start">
                  <span className="text-white/26 text-xs tracking-[.25em] pt-1">{n}</span>
                  <strong className="font-accent tracking-[.12em] text-white">{title}</strong>
                  <p className="text-white/45 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="silver-wave silver-wave-left"/>
        <div className="container-premium relative z-10">
          <p className="section-kicker mb-4">FROM BRAND TO BUSINESS</p>
          <h2 className="gs-display text-4xl md:text-6xl max-w-5xl mb-12">
            NO SOLO CREAMOS TU MARCA.<br/>
            <span className="metal-text">CONSTRUIMOS EL SISTEMA PARA HACERLA CRECER.</span>
          </h2>
          <div className="gs-flow">
            {flow.map((x,i)=>(
              <React.Fragment key={x}>
                <div className="gs-flow-item">{x}</div>
                {i<flow.length-1 && <div className="gs-flow-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#080808]">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 editorial-frame overflow-hidden">
            {[
              [Smartphone,'PRODUCTOS DIGITALES','Web, apps y experiencias conectadas al negocio.'],
              [Bot,'AUTOMATIZACIÓN','Procesos que trabajan con menos fricción y más trazabilidad.'],
              [Megaphone,'GROWTH SYSTEMS','Contenido, campañas y funnels conectados al CRM.'],
              [BadgeDollarSign,'MONETIZACIÓN','Pagos, membresías, suscripciones y ventas digitales.']
            ].map(([Icon,title,text])=>(
              <div className="gs-panel p-8 min-h-[240px]" key={title}>
                <Icon size={22} className="text-[#d7dbe0] mb-10"/>
                <h3 className="font-accent tracking-[.12em] text-white mb-3">{title}</h3>
                <p className="text-white/44 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="editorial-frame p-8 md:p-14 grid lg:grid-cols-[1fr_auto] gap-10 items-end">
            <div>
              <p className="section-kicker mb-4">CONSTRUYAMOS</p>
              <h2 className="gs-display text-4xl md:text-6xl mb-5">TU PRÓXIMA <span className="metal-text">PLATAFORMA</span></h2>
              <p className="text-white/48 max-w-2xl">Proyectos cotizados según alcance, integraciones y nivel de personalización.</p>
            </div>
            <Link to="/contact" className="btn-metal inline-flex items-center gap-2">Construir mi plataforma <ArrowRight size={17}/></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
